import classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import Input from "../Input/Input";
import Styles from "./Date.module.scss";
import i18n from "@/i18n";
import {
  is18orOlder,
  isDate,
  isNotFutureDate,
  isValidDay,
  isValidMonth,
  isValidYear,
} from "../../validations/validations";

interface props {
  name: string;
  value: [string, string, string];
  onChange: (val: [string, string, string]) => void;
  onValidate?: (err: string) => void;
  title?: string | React.ReactNode;
  containerClass?: string;
  dayClass?: string;
  monthClass?: string;
  yearClass?: string;
  withoutTitles?: boolean;
  required?: boolean;
  futureDate?: boolean;
  eighteenOrOlder?: boolean;
  autoFocus?: boolean;
}

const Date = ({
  name,
  value,
  onChange,
  title,
  containerClass,
  dayClass,
  monthClass,
  yearClass,
  withoutTitles,
  onValidate,
  required,
  futureDate,
  eighteenOrOlder,
  autoFocus,
}: props) => {
  const { t } = i18n;
  const [error, setError] = useState<string | undefined>(undefined);
  const dayRef = useRef<HTMLInputElement>(null);
  const monthRef = useRef<HTMLInputElement>(null);
  const yearRef = useRef<HTMLInputElement>(null);

  const day = value[0];
  const month = value[1];
  const year = value[2];

  useEffect(() => {
    if (autoFocus) {
      dayRef.current && dayRef.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (onValidate) {
      onValidate(error || "");
    }
    //eslint-disable-next-line
  }, [error]);

  // Automatically focus the next input when typing the date.
  useEffect(() => {
    // just change focus when the day input was focused before and month has no value
    if (day.length === 2 && month.length === 0 && dayRef.current === document.activeElement) {
      monthRef.current && monthRef.current.focus();
    }
    // just change focus when the month input was focused before and year has no value
    if (month.length === 2 && year.length === 0 && monthRef.current === document.activeElement) {
      yearRef.current && yearRef.current.focus();
    }
  }, [day, month, year]);

  const onChangeDay = (val: string) => {
    if (val === "" || isValidDay(val)) {
      // Needed to tell typescript, that the new array has an exact length of three. Without that, typescript is not able to understand this, because the spread syntax can be used to create a different array than the original one.
      const newValue = [...value] as typeof value;
      newValue[0] = val;
      onChange(newValue);
      setError(undefined);
    }
  };

  const onChangeMonth = (val: string) => {
    if (val === "" || isValidMonth(val)) {
      const newValue = [...value] as typeof value;
      newValue[1] = val;
      onChange(newValue);
      setError(undefined);
    }
  };

  const onChangeYear = (val: string) => {
    if (val === "" || isValidYear(val)) {
      const newValue = [...value] as typeof value;
      newValue[2] = val;
      onChange(newValue);
      setError(undefined);
    }
  };

  const validateDay = () => {
    if (!required && !day) {
      return;
    }
    if (!day || day.length < 2) {
      setError(t("validations.invalidDate") || undefined);
      return;
    }
    setErrorIfInvalid();
  };

  const validateMonth = () => {
    if (!required && !day && !month) {
      return;
    }
    if (!month || month.length < 2) {
      setError(t("validations.invalidDate") || undefined);
      return;
    }
    setErrorIfInvalid();
  };

  const validateYear = () => {
    if (!required && !day && !month && !year) {
      return;
    }
    if (!year || year.length < 4) {
      setError(t("validations.invalidDate") || undefined);
      return;
    }
    setErrorIfInvalid();
  };

  const setErrorIfInvalid = () => {
    if (day.length === 2 && month.length === 2 && year.length === 4) {
      let error = isDate(year, month, day);
      if (!error && futureDate) {
        error = isNotFutureDate(year, month, day);
      }
      if (!error && eighteenOrOlder) {
        error = is18orOlder(year, month, day);
      }
      setError(error);
    } else if (day.length > 0 && month.length > 0 && year.length > 0) {
      setError(t("validations.invalidDate") || undefined);
    }
  };

  return (
    <div className={classnames(Styles.dateContainer, containerClass)}>
      {title && <span className={Styles.title}>{title}</span>}
      <div className={Styles.rowContainer}>
        <Input
          className={classnames(Styles.dayInput, dayClass)}
          name={`${name}_day`}
          value={day}
          onChange={(e) => onChangeDay(e.target.value)}
          placeholder={"TT"}
          reference={dayRef}
          onBlur={validateDay}
          maxLength={2}
          title={!withoutTitles && t("titles.day").toString()}
          error={!!error}
          showValidation={true}
          numeric
        ></Input>
        <Input
          className={classnames(Styles.monthInput, monthClass)}
          name={`${name}_month`}
          value={month}
          onChange={(e) => onChangeMonth(e.target.value)}
          placeholder={"MM"}
          reference={monthRef}
          onBlur={validateMonth}
          maxLength={2}
          title={!withoutTitles && t("titles.month").toString()}
          error={!!error}
          showValidation={true}
          numeric
        ></Input>
        <Input
          className={classnames(Styles.yearInput, yearClass)}
          name={`${name}_year`}
          value={year}
          onChange={(e) => onChangeYear(e.target.value)}
          placeholder={"JJJJ"}
          reference={yearRef}
          onBlur={validateYear}
          maxLength={4}
          title={!withoutTitles && t("titles.year").toString()}
          error={!!error}
          showValidation={true}
          numeric
        ></Input>
      </div>
      {error && (
        <div className={Styles.dateError} role={"alert"}>
          {error}
        </div>
      )}
    </div>
  );
};

export default Date;
